import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { contractTemplatesAPI } from '../../services/api';

export const fetchContractTemplates = createAsyncThunk(
  'contractTemplates/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const res = await contractTemplatesAPI.list(params);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch contract templates');
    }
  }
);

export const fetchContractTemplate = createAsyncThunk(
  'contractTemplates/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const res = await contractTemplatesAPI.get(id);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch contract template');
    }
  }
);

export const createContractTemplate = createAsyncThunk(
  'contractTemplates/create',
  async (formData, { rejectWithValue }) => {
    try {
      const res = await contractTemplatesAPI.create(formData);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to create contract template');
    }
  }
);

export const updateContractTemplate = createAsyncThunk(
  'contractTemplates/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await contractTemplatesAPI.update(id, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to update contract template');
    }
  }
);

export const duplicateContractTemplate = createAsyncThunk(
  'contractTemplates/duplicate',
  async (id, { rejectWithValue }) => {
    try {
      const res = await contractTemplatesAPI.duplicate(id);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to duplicate contract template');
    }
  }
);

export const deleteContractTemplate = createAsyncThunk(
  'contractTemplates/delete',
  async (id, { rejectWithValue }) => {
    try {
      await contractTemplatesAPI.delete(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to delete contract template');
    }
  }
);

const contractTemplatesSlice = createSlice({
  name: 'contractTemplates',
  initialState: {
    items: [],
    current: null,
    pagination: null,
    loading: false,
    currentLoading: false,
    error: null,
  },
  reducers: {
    clearCurrent: (state) => {
      state.current = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchContractTemplates.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchContractTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.templates;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchContractTemplates.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      // Fetch one
      .addCase(fetchContractTemplate.pending, (state) => { state.currentLoading = true; })
      .addCase(fetchContractTemplate.fulfilled, (state, action) => {
        state.currentLoading = false;
        state.current = action.payload.template;
      })
      .addCase(fetchContractTemplate.rejected, (state, action) => { state.currentLoading = false; state.error = action.payload; })
      // Create
      .addCase(createContractTemplate.fulfilled, (state, action) => {
        state.items.unshift(action.payload.template);
      })
      // Update
      .addCase(updateContractTemplate.fulfilled, (state, action) => {
        state.current = action.payload.template;
        const idx = state.items.findIndex(t => t._id === action.payload.template._id);
        if (idx !== -1) state.items[idx] = action.payload.template;
      })
      // Duplicate
      .addCase(duplicateContractTemplate.fulfilled, (state, action) => {
        state.items.unshift(action.payload.template);
      })
      // Delete
      .addCase(deleteContractTemplate.fulfilled, (state, action) => {
        state.items = state.items.filter(t => t._id !== action.payload);
      });
  },
});

export const { clearCurrent } = contractTemplatesSlice.actions;
export default contractTemplatesSlice.reducer;
